// src/pages/AiGame404.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Styles.css";

const randomSignal = () => (40 + Math.floor(Math.random() * 60)); // 40–99

export default function AiGame404() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [scanCount, setScanCount] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const maxScans = 3;

  useEffect(() => {
    setLogs([
      { id: 1, from: "system", text: "⚠ 404: Page lost in the void." },
      {
        id: 2,
        from: "ai",
        text: "Hi, I’m gupsy-AI. I’ll try to track down the missing page for you."
      },
      {
        id: 3,
        from: "ai",
        text: "Run a few scans with me and we’ll see where it went..."
      }
    ]);
  }, []);

  const addLog = (from, text) => {
    setLogs((prev) => [
      ...prev,
      { id: prev.length + 1, from, text }
    ]);
  };

  const handleScan = () => {
    if (isScanning || scanCount >= maxScans) return;

    setIsScanning(true);
    const currentScan = scanCount + 1;

    addLog("you", `Scan #${currentScan} initiated.`);
    addLog("ai", "Spinning up quantum crawlers…");

    setTimeout(() => {
      const signal = randomSignal();
      addLog(
        "ai",
        `Signal strength: ${signal}%. Analyzing broken links, cache echoes, and ghost routes…`
      );
    }, 500);

    setTimeout(() => {
      if (currentScan < maxScans) {
        addLog(
          "ai",
          currentScan === 1
            ? "Hmm… I’m picking up traces of this page, but it seems to have drifted off."
            : "Still no stable lock. Either this page never existed, or it ascended to a higher dimension. 🛸"
        );
      } else {
        addLog(
          "ai",
          "Okay, I’ve scanned everywhere. This page is officially lost. Let me get you somewhere safe."
        );
        addLog(
          "system",
          "💡 Tip: If you reached this from a link, it might be broken or outdated."
        );
      }

      setScanCount(currentScan);
      setIsScanning(false);
    }, 1500);
  };

  const handleHome = () => {
    navigate("/");
  };

  const canScanMore = scanCount < maxScans;

  return (
    <div className="ai404-wrapper">
      <div className="ai404-card">
        <div className="ai404-header">
          <span className="ai404-pill">Error 404</span>
          <h1 className="ai404-title">Page Lost in the Matrix</h1>
          <p className="ai404-subtitle">
            The URL you tried to reach doesn’t exist, moved, or was never born.
            But hey, let’s turn this into a mini AI mission instead of a dead end.
          </p>
        </div>

        <div className="ai404-console">
          <div className="ai404-console-header">
            <span className="dot" />
            <span className="dot" />
            <span className="dot" />
            <span className="ai404-console-title">gupsy-AI Debug Console</span>
          </div>
          <div className="ai404-console-body">
            {logs.map((log) => (
              <div
                key={log.id}
                className={`ai404-log ai404-log-${log.from}`}
              >
                <span className="ai404-log-label">
                  {log.from === "you"
                    ? "You"
                    : log.from === "ai"
                    ? "gupsy-AI"
                    : "System"}
                  :
                </span>
                <span className="ai404-log-text">{log.text}</span>
              </div>
            ))}
            {isScanning && (
              <div className="ai404-log ai404-log-ai">
                <span className="ai404-log-label">gupsy-AI:</span>
                <span className="ai404-log-text ai404-typing">
                  Scanning<span>.</span><span>.</span><span>.</span>
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="ai404-actions">
          <button
            className="ai404-button primary"
            onClick={handleScan}
            disabled={!canScanMore || isScanning}
          >
            {scanCount === 0 && !isScanning && "🔍 Run first scan"}
            {scanCount > 0 && canScanMore && !isScanning && "🔁 Run another scan"}
            {isScanning && "Scanning…"}
            {!canScanMore && !isScanning && "Scans complete"}
          </button>

          <button className="ai404-button ghost" onClick={handleHome}>
            ⬅ Take me home
          </button>
        </div>

        <p className="ai404-footer-hint">
          P.S. If this keeps happening, the link might be wrong or the page was removed.
        </p>
      </div>
    </div>
  );
}
